/**
 * Result Actions Component
 * Displays quick action buttons for MCP tool results
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import './ResultActions.css';

interface Action {
  label: string;
  icon: string;
  action: () => void;
}

interface ResultActionsProps {
  result: any;
  toolName: string;
}

export const ResultActions: React.FC<ResultActionsProps> = ({
  result,
  toolName,
}) => {
  const navigate = useNavigate();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    notification.success({
      message: 'Kopirano!',
      description: `${label} je kopiran u clipboard`,
      duration: 2,
    });
  };

  const getActions = (): Action[] => {
    // Kanban board actions
    if (toolName.includes('kanban') && result.boards) {
      const actions: Action[] = [];
      result.boards.slice(0, 3).forEach((board: any) => {
        actions.push({
          label: `Otvori ${board.title}`,
          icon: '📋',
          action: () => navigate(`/apps/kanban/${board.slug}`),
        });
      });
      return actions;
    }

    // Lead actions
    if (toolName.includes('lead') && result.leads) {
      const actions: Action[] = [];
      result.leads.slice(0, 3).forEach((lead: any) => {
        actions.push({
          label: `Otvori ${lead.name}`,
          icon: '👤',
          action: () => navigate(`/apps/leads/${lead.id}`),
        });
        actions.push({
          label: `Kopiraj ID (${lead.name})`,
          icon: '📋',
          action: () => copyToClipboard(lead.id, 'Lead ID'),
        });
      });
      return actions;
    }

    // Ticket actions
    if (toolName.includes('ticket') && result.tickets) {
      const actions: Action[] = [];
      result.tickets.slice(0, 3).forEach((ticket: any) => {
        actions.push({
          label: `Otvori #${ticket.unique_id || ticket.title.substring(0, 20)}`,
          icon: '🎫',
          action: () => navigate(`/apps/tickets/${ticket.id}`),
        });
        actions.push({
          label: `Kopiraj ID (#${ticket.unique_id || 'ID'})`,
          icon: '📋',
          action: () => copyToClipboard(ticket.id, 'Ticket ID'),
        });
      });
      return actions;
    }

    // Calendar event actions
    if (result.calendar_events) {
      const actions: Action[] = [];
      result.calendar_events.slice(0, 3).forEach((event: any) => {
        actions.push({
          label: `Otvori ${event.title}`,
          icon: '📅',
          action: () => navigate(`/apps/calendar/${event.id}`),
        });
      });
      return actions;
    }

    return [];
  };

  const actions = getActions();

  if (actions.length === 0) return null;

  return (
    <div className="result-actions">
      <div className="actions-label">Brze akcije:</div>
      <div className="actions-list">
        {actions.map((action, index) => (
          <button key={index} className="action-btn" onClick={action.action}>
            <span className="action-icon">{action.icon}</span>
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
};
